/* ===========================================================
   Lucraa — Landing page (pública)
   Abaixo do hero, uma ESCADA DE PROVA (não um tour): tese (ROAS≠lucro)
   → método (cascata auditável) → granularidade (campanha/produto) →
   o triângulo do ROAS → diagnóstico → confiança → preço → prova social
   + FAQ → fechamento. Hero (hero.tsx) é intocável. Tema escuro premium:
   fundo escuro + cards claros "flutuando" como screenshots vivos.
   =========================================================== */
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { useApiQuery } from "../../api/useApiQuery";
import { fmt } from "../../domain/format";
import { Icon } from "../../components/ui.jsx";
import { SiteFooter } from "../../components/site-footer";
import { FAQ } from "./landing.data";
import { LandingHeader, LandingHero } from "./hero";
import { ComposicaoLucroMock, CampanhasMock, ProdutosMock, MonitoresMock } from "./modulos";
import { LpSectionHead, LpVs, LpRoasTriangulo } from "./lp";
import { LandingDepoimento } from "./depoimento";
import { LandingProva } from "./prova";
import { LandingSeguranca } from "./seguranca";
import { useReveal } from "./reveal";
import { useLandingTracking } from "../../analytics/landing-tracking";

const F = fmt;
const roasFmt = F.roas;

const Divider = () => <hr className="lp-divider" aria-hidden="true" />;

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map(({ pergunta, resposta }) => ({
    "@type": "Question",
    name: pergunta,
    acceptedAnswer: { "@type": "Answer", text: resposta },
  })),
};

export function Landing({
  onEntrar,
  onIrDemo,
  abrirDemoInicial = false,
}: {
  onEntrar: () => void;
  /** CTA da demo: leva pro app (/app/inicio), onde o gate aparece SOBRE o painel
   *  borrado e dá início ao fluxo guiado (jornada nova). Sem popup na landing. */
  onIrDemo: () => void;
  /** Deep-link /demo: entra direto no fluxo da demo (no app). */
  abrirDemoInicial?: boolean;
}) {
  const { data: D } = useApiQuery("landing", api.getLanding);
  useEffect(() => {
    if (abrirDemoInicial) onIrDemo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const abrirDemo = onIrDemo;
  // re-roda quando os dados chegam: as seções `.lp-stagger` montam sob `{D && …}`,
  // então no 1º render (D ainda undefined) elas não existem para observar.
  useReveal(Boolean(D));
  useLandingTracking(); // instrumentação Mixpanel da landing (decisions.md §34)

  // CTA fixo no rodapé (só mobile) — aparece quando o CTA primário do hero sai de vista.
  const [stickyCta, setStickyCta] = useState(false);
  useEffect(() => {
    const alvo = document.querySelector(".lp-hero-cta");
    if (!alvo) return;
    const obs = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e) setStickyCta(!e.isIntersecting);
    });
    obs.observe(alvo);
    return () => obs.disconnect();
  }, []);

  // Tese da seção #problema — uma campanha REAL em prejuízo. Só existe quando há
  // prejuízo de fato; sem isso, a seção inteira se omite (o card "depois" afirma
  // "queima margem/pause", então não pode receber um lucro positivo). No mock
  // Koloni há campanha negativa, então a seção sempre aparece.
  const campPrejuizo = D?.ads.campanhas.find((c) => c.lucroLiquido < 0);

  return (
    <div className="lp-root">
      <LandingHeader onTestar={abrirDemo} onEntrar={onEntrar} />

      <main>
        <LandingHero D={D} onTestar={abrirDemo} />

        {/* Banda de prova/confiança — logo abaixo do hero (estática, não espera D) */}
        <LandingProva />

        {D && (
          <>
            {/* 1 · A TESE — ROAS não é lucro */}
            <section id="problema" className="lp-section" aria-labelledby="lp-h2-problema">
              <div className="lp-wrap">
                <LpSectionHead
                  eyebrow="O problema"
                  titleId="lp-h2-problema"
                  title={
                    campPrejuizo
                      ? `No gerenciador, ROAS ${roasFmt(campPrejuizo.roasInformado)}. No seu caixa, prejuízo.`
                      : "ROAS alto não é lucro. Veja o que sobra no caixa."
                  }
                />
                {campPrejuizo && (
                  <>
                    <LpVs
                      antes={
                        <>
                          <span className="pill pill-line">No gerenciador de anúncios</span>
                          <div className="lp-vs-meta">
                            <span>
                              {campPrejuizo.plataforma} · {campPrejuizo.nome}
                            </span>
                            <span className="lp-vs-cap" style={{ marginLeft: "auto" }}>
                              {F.brl(campPrejuizo.investido)} investidos
                            </span>
                          </div>
                          <div className="lp-big-num" style={{ marginTop: 14 }}>
                            {roasFmt(campPrejuizo.roasInformado)}
                          </div>
                          <span className="lp-vs-cap">ROAS de receita</span>
                          <p
                            style={{
                              fontSize: 14.5,
                              color: "var(--ink-soft)",
                              margin: "18px 0 0",
                              lineHeight: 1.5,
                            }}
                          >
                            Parece saudável: receita atribuída ÷ gasto, sem nenhum custo descontado.
                          </p>
                        </>
                      }
                      depois={
                        <>
                          <span className="pill pill-lucro">No Lucraa</span>
                          <div className="lp-vs-meta">
                            <span>
                              {campPrejuizo.plataforma} · {campPrejuizo.nome}
                            </span>
                            <span className="lp-vs-cap" style={{ marginLeft: "auto" }}>
                              mesma campanha
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: 22,
                              marginTop: 14,
                            }}
                          >
                            <div>
                              <div className="lp-big-num">{roasFmt(campPrejuizo.roasReal)}</div>
                              <span className="lp-vs-cap">ROAS real</span>
                            </div>
                            <div>
                              <div className="lp-big-num" style={{ color: "var(--prejuizo)" }}>
                                {F.brl(campPrejuizo.lucroLiquido, { sign: true })}
                              </div>
                              <span className="lp-vs-cap">lucro no mês</span>
                            </div>
                          </div>
                          <p
                            style={{
                              fontSize: 14.5,
                              color: "var(--ink-soft)",
                              margin: "18px 0 0",
                              lineHeight: 1.5,
                            }}
                          >
                            Abaixo do breakeven: descontando CMV, frete e imposto, ela queima
                            margem. Recomendação do Lucraa: pause ou reprecifique.
                          </p>
                        </>
                      }
                    />
                    <p className="lp-footnote">
                      Mesma campanha, números da loja de exemplo {D.nuvemshop.loja} — calculados ao
                      vivo.
                    </p>
                    <div style={{ textAlign: "center" }}>
                      <a href="#meu-lucro" className="lp-anchor">
                        Veja como o Lucraa chega no lucro <span aria-hidden="true">↓</span>
                      </a>
                    </div>
                  </>
                )}
              </div>
            </section>

            <Divider />

            {/* 2 · O MÉTODO — cascata auditável full-width e interativa (id #meu-lucro p/ a nav do hero) */}
            <section id="meu-lucro" className="lp-section" aria-labelledby="lp-h2-metodo">
              <div className="lp-wrap">
                <LpSectionHead
                  eyebrow="Meu Lucro · o método"
                  titleId="lp-h2-metodo"
                  title="Da receita bruta ao lucro das vendas — audite cada degrau."
                  lead="O mesmo cálculo da sua planilha de domingo — só que sozinho, todo dia e sem caixa-preta. Saem CMV, frete, imposto, embalagem e mídia; o que sobra é o lucro das vendas, o número que paga o boleto."
                />
                <div
                  className="lp-casc-wrap lp-stagger"
                  style={{ marginTop: "var(--lp-gap-head)" }}
                >
                  <ComposicaoLucroMock steps={D.waterfall} loja={D.nuvemshop.loja} />
                </div>
                <div style={{ textAlign: "center" }}>
                  <a href="#campanhas" className="lp-anchor">
                    Veja por campanha e por produto <span aria-hidden="true">↓</span>
                  </a>
                </div>
              </div>
            </section>

            <Divider />

            {/* 2b · ADEUS PLANILHA — comparação planilha × Lucraa (mesmo cálculo, sem o trabalho) */}
            <section id="vs-planilha" className="lp-section" aria-labelledby="lp-h2-planilha">
              <div className="lp-wrap">
                <LpSectionHead
                  center
                  eyebrow="Adeus, planilha"
                  titleId="lp-h2-planilha"
                  title="O cálculo da sua planilha de domingo — automático, todo dia."
                  lead="A mesma conta que você faz na mão, sem o trabalho e sem o erro de fórmula — o Lucraa atualiza sozinho a cada venda."
                />
                <LpVs
                  antes={
                    <>
                      <span className="pill pill-line">Na planilha de domingo</span>
                      <ul className="lp-cmp-list">
                        {[
                          "Feita na mão, toda semana",
                          "Fórmula quebra e ninguém percebe",
                          "Só receita — não desconta custo nem mídia",
                          "Desatualiza já na segunda",
                        ].map((t) => (
                          <li key={t} className="lp-cmp-item lp-cmp-item--no">
                            <span className="lp-cmp-dot" aria-hidden="true" />
                            {t}
                          </li>
                        ))}
                      </ul>
                    </>
                  }
                  depois={
                    <>
                      <span className="pill pill-lucro">No Lucraa</span>
                      <ul className="lp-cmp-list">
                        {[
                          "Automática, atualiza a cada venda",
                          "Auditável degrau a degrau",
                          "Desconta CMV, frete, imposto e mídia",
                          "Lucro real, sempre do dia",
                        ].map((t) => (
                          <li key={t} className="lp-cmp-item">
                            <Icon
                              name="check"
                              size={15}
                              color="var(--lucro)"
                              strokeWidth={2.6}
                              style={{ flex: "none", marginTop: 1 }}
                            />
                            {t}
                          </li>
                        ))}
                      </ul>
                    </>
                  }
                />
              </div>
            </section>

            <Divider />

            {/* 3 · GRANULARIDADE — campanha e produto */}
            <section id="campanhas" className="lp-section" aria-labelledby="lp-h2-camp">
              <div className="lp-wrap">
                <LpSectionHead
                  eyebrow="MER · ROAS real"
                  titleId="lp-h2-camp"
                  title="ROAS real e lucro — por campanha e por produto."
                  lead="O Lucraa cruza Meta e Google com cada pedido e conta cada venda uma vez. Por campanha, o lucro das vendas; por produto, o ROAS de equilíbrio que diz qual aguenta escalar."
                />
                <div className="lp-grid-2 lp-stagger">
                  <CampanhasMock ads={D.ads} />
                  <ProdutosMock produtos={D.nuvemshop.produtos} />
                </div>
                <div style={{ textAlign: "center", marginTop: 28 }}>
                  <a href="#roas" className="lp-anchor" style={{ marginTop: 0 }}>
                    Por que dois ROAS? <span aria-hidden="true">↓</span>
                  </a>
                </div>
              </div>
            </section>

            <Divider />

            {/* 4 · O TRIÂNGULO DO ROAS */}
            <section id="roas" className="lp-section" aria-labelledby="lp-h2-roas">
              <div className="lp-wrap">
                <LpSectionHead
                  center
                  eyebrow="O número que decide"
                  titleId="lp-h2-roas"
                  title="ROAS informado, ROAS real e ROAS de equilíbrio."
                  lead="Os dois primeiros estão certos — usam janelas diferentes. O terceiro diz a verdade: abaixo dele, o anúncio queima margem mesmo vendendo."
                />
                <LpRoasTriangulo
                  informado={D.derivados.roasMedio}
                  real={D.derivados.roasReal}
                  equilibrio={D.derivados.roasEquilibrio}
                />
                <p className="lp-roas-rule">
                  Acima do equilíbrio, vale escalar.{" "}
                  <strong>Abaixo, é prejuízo disfarçado de venda.</strong>
                </p>
              </div>
            </section>

            <Divider />

            {/* 5 · DIAGNÓSTICO — monitores 24/7 */}
            <section id="diagnostico" className="lp-section" aria-labelledby="lp-h2-diag">
              <div className="lp-wrap">
                <LpSectionHead
                  eyebrow="Diagnóstico contínuo"
                  titleId="lp-h2-diag"
                  title="Onde você está perdendo dinheiro agora."
                  lead={`Cada alerta chega com o problema em reais e o caminho para resolver. Hoje a loja de exemplo tem ${D.derivados.monitores.length} ${D.derivados.monitores.length === 1 ? "alerta aberto" : "alertas abertos"} — ${F.brl(D.derivados.recuperavelMes)}/mês em risco.`}
                />
                <MonitoresMock monitores={D.derivados.monitores} />
              </div>
            </section>

            <Divider />

            {/* 6 · CONFIANÇA */}
            <LandingSeguranca />

            <Divider />
          </>
        )}

        {/* 7 · PROVA HUMANA — depoimento + nota do fundador (estático) */}
        <Divider />
        <LandingDepoimento />

        {/* 8 · FAQ — estático (renderiza antes dos dados) + JSON-LD */}
        <section id="faq" className="lp-section" aria-labelledby="lp-h2-faq">
          <div className="lp-wrap">
            <LpSectionHead
              center
              eyebrow="Perguntas frequentes"
              titleId="lp-h2-faq"
              title="O que todo lojista pergunta antes de conectar a loja."
            />
            <div className="lp-faq">
              {FAQ.map(({ pergunta, resposta }) => (
                <details key={pergunta} className="card lp-faq-item">
                  <summary>{pergunta}</summary>
                  <p>{resposta}</p>
                </details>
              ))}
            </div>
          </div>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }}
          />
        </section>

        <Divider />

        {/* 9 · FECHAMENTO — CTA único (waitlist) */}
        <section id="comecar" className="lp-wrap lp-section" aria-labelledby="lp-h2-cta">
          <div className="lp-cta-final on-dark">
            <div className="lp-cta-grid">
              {/* ZONA ESQUERDA — argumento + ação */}
              <div className="lp-cta-pitch">
                <span className="lp-cta-eyebrow">Comece agora</span>
                <h2 id="lp-h2-cta">Pare de adivinhar o seu lucro.</h2>
                <p className="lp-cta-sub">
                  Conecte a Nuvemshop e veja, no primeiro dia, quanto cada campanha e cada produto
                  realmente deixou no caixa — descontando CMV, frete, imposto e mídia.
                </p>

                <div className="lp-cta-actions">
                  <button
                    className="btn btn-lg lp-btn-inverse lp-cta-btn"
                    data-cta="fechamento_signup"
                    onClick={abrirDemo}
                  >
                    Ver demonstração ao vivo
                    <span className="lp-cta-btn-arrow" aria-hidden="true">
                      ›
                    </span>
                  </button>
                </div>

                <ul className="lp-cta-chips">
                  <li>
                    <Icon name="check" size={13} color="var(--mint)" strokeWidth={2.6} /> Sem cartão
                  </li>
                  <li>
                    <Icon name="check" size={13} color="var(--mint)" strokeWidth={2.6} /> 7 dias
                    completos
                  </li>
                  <li>
                    <Icon name="check" size={13} color="var(--mint)" strokeWidth={2.6} /> Cancele em
                    2 cliques
                  </li>
                </ul>
                <p className="lp-cta-legal">
                  Demonstração com os números no porte da sua loja · sem cadastro.
                </p>
              </div>

              {/* ZONA DIREITA — a prova: recibo auditável. A moldura existe sempre; os números
                  entram sob {D &&}, com skeleton no loading p/ a composição não colapsar. */}
              <div
                className="lp-cta-receipt"
                role="group"
                aria-label="Potencial a recuperar na loja de exemplo"
              >
                <div className="lp-cta-receipt-head">
                  <span className="lp-cta-receipt-dot" />
                  Loja de exemplo · ao vivo
                </div>

                {D ? (
                  <>
                    <ul className="lp-cta-ledger" aria-hidden="true">
                      {D.derivados.monitores.slice(0, 3).map((m) => (
                        <li key={m.id} className="lp-cta-ledger-row">
                          <span className="lp-cta-ledger-label">{m.tag}</span>
                          <span className="lp-cta-ledger-rule" />
                          <span className="num lp-cta-ledger-val">{F.brl(m.potencial)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="lp-cta-receipt-total">
                      <span className="lp-cta-receipt-label">Potencial a recuperar</span>
                      <div className="lp-num-hero lp-cta-receipt-num">
                        {F.brl(D.derivados.recuperavelMes)}
                        <span className="lp-cta-receipt-per">/mês</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="lp-cta-receipt-skel" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                    <span className="lp-cta-receipt-skel-num" />
                  </div>
                )}

                <div className="lp-cta-receipt-foot">
                  <Icon name="search" size={14} color="var(--mint)" />
                  Soma dos alertas abertos do Diagnóstico
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* RODAPÉ — componente único, igual ao das páginas de conteúdo */}
      <SiteFooter />

      {/* CTA fixo (mobile) — visível só após o hero sair de vista; oculto no desktop via CSS */}
      <div className={"lp-sticky-cta" + (stickyCta ? " is-on" : "")} aria-hidden={!stickyCta}>
        <button className="btn btn-primary btn-block" data-cta="sticky_signup" onClick={abrirDemo}>
          Ver demonstração ao vivo
        </button>
      </div>
    </div>
  );
}
